import { Component, OnDestroy, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { ConfirmationComponent } from '../../../shared/confirmation/confirmation.component';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { FolderStatus, labelsByFolderStatus } from '../../../shared/models/enums/folderStatus.enum';
import { MaritalStatus, labelsByMaritalStatus } from '../../../shared/models/enums/marital-status.enum';
import { ReasonOrientationEnding, labelsByReasonOrientationEnding } from '../../../shared/models/enums/reason-orientation-ending.enum';
import { SupportTypes } from '../../../shared/models/enums/support-type.enum';
import { ItemHistorizable } from '../../../shared/models/itemHistorizable';
import { StructureDto } from '../../../shared/models/structure.dto';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { BeneficiaryComplementsService } from '../../../shared/services/beneficiaryComplements.service';
import { DateService } from '../../../shared/services/date.service';
import { HealthInsurancesService } from '../../../shared/services/health-insurances.service';
import { IntensityService } from '../../../shared/services/intensity.service';
import { StringUtils } from '../../../shared/services/stringUtils.service';
import { StructureService } from '../../../shared/services/structure.service';
import { SupportTypeService } from '../../../shared/services/supportType.service';
import { UserService } from '../../../shared/services/user.service';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { DateValidators } from '../../../shared/validators/date.validators';
import { InputValidators } from '../../../shared/validators/input.validators';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryIntensityDto } from '../../dto/beneficiary-intensity.dto';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { IntensityDto } from '../../dto/intensity.dto';
import { OrientationDto } from '../../dto/orientationDto';
import { ReferentDto } from '../../dto/referent.dto';
import { SupportDto } from '../../dto/supportDto';
import { SupportTypeDto } from '../../dto/supportTypeDto';
import { BeneficiaryComplement } from './beneficiaryComplement';
import { BeneficiaryComplementSaveDto } from './beneficiaryComplementSaveDto';
import { Complement } from './complement';

type supportFormType = {
  id: FormControl<number | null>;
  beginDateSupport: FormControl<Date>;
  endDateSupport: FormControl<Date>;
  supportTypeId: FormControl<number>;
  typeSupportDetail: FormControl<string>;
};

type orientationFormType = {
  id: FormControl<number | null>;
  orientationBeginDate: FormControl<Date>;
  orientationEndDate: FormControl<Date>;
  structureId: FormControl<number>;
  reasonOrientationEnding: FormControl<string>;
};

type referentFormType = {
  id: FormControl<number | null>;
  beginDateReferent: FormControl<Date>;
  endDateReferent: FormControl<Date>;
  referent: FormControl<number>;
};

type intensityFormType = {
  id: FormControl<number | null>;
  beginDate: FormControl<Date>;
  endDate: FormControl<Date>;
  intensityId: FormControl<number>;
};

@Component({
  selector: 'app-complement',
  templateUrl: './complement.component.html',
  styleUrls: ['./complement.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ComplementComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  stringAscOrder = StringUtils.stringAscOrder;

  $reasonOrientationEnding = Utils.castTo<ReasonOrientationEnding>();

  public complementForm: FormGroup<{
    status: FormControl<string>;
    maritalStatus: FormControl<string>;
    childrenCount: FormControl<number>;
    dependentsNumber: FormControl<number>;
    singleParent: FormControl<boolean>;
    rsaRightBeginDate: FormControl<Date>;
    healthInsuranceId: FormControl<number>;
    supportForm: FormGroup<supportFormType>;
    orientationForm: FormGroup<orientationFormType>;
    referentForm: FormGroup<referentFormType>;
    intensityForm: FormGroup<intensityFormType>;
  }>;
  public submitted: boolean = false;

  public readonly folderStatusMap: Map<FolderStatus, string> = labelsByFolderStatus;
  public readonly maritalStatusMap: Map<MaritalStatus, string> = labelsByMaritalStatus;
  public readonly reasonOrientationEndingMap: Map<ReasonOrientationEnding, string> = labelsByReasonOrientationEnding;
  public readonly StringUtils: StringUtils = StringUtils;
  public user: UserDto;
  public isInvalidSupport: boolean;
  public beneficiaryStructureWithoutBeneficiarySupport: DisplayLabel;

  public referentHistoryInvalid: boolean = false;
  public intensityHistoryInvalid: boolean = false;
  public orientationHistoryInvalid: boolean = false;
  public supportHistoryInvalid: boolean = false;
  public enabled: boolean = false;
  public hasHistorizedReferent: boolean = false;
  public hasHistorizedIntensities: boolean = false;
  public hasHistorizedOrientations: boolean = false;
  public hasHistorizedSupports: boolean = false;

  public structures: DisplayLabel[] = [];
  public userList: UserDto[] = [];
  public healthInsurances: DisplayLabel[] = [];
  public supportTypes: DisplayLabel[];
  public intensities: IntensityDto[];
  public orientations: OrientationDto[];
  public supports: SupportDto[];

  private beneficiaryComplement: BeneficiaryComplement;
  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public beneficiaryReferents: ReferentDto[] = [];
  public beneficiaryIntensities: BeneficiaryIntensityDto[] = [];
  public beneficiaryOrientations: OrientationDto[] = [];
  public beneficiarySupports: SupportDto[] = [];

  public canDisplayHistorizeButton: boolean = false;
  public accessMode: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userService: UserService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly structureService: StructureService,
    private readonly beneficiaryComplementsService: BeneficiaryComplementsService,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private readonly healthInsurancesService: HealthInsurancesService,
    private readonly supportTypeService: SupportTypeService,
    private readonly dateService: DateService,
    private readonly intensityService: IntensityService,
  ) {
    super();
  }

  // *********************************************************************************************
  // * INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT *
  // * INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT INIT *
  // *********************************************************************************************

  // *********************************************************************************************
  // * Init angular functions

  public async ngOnInit(): Promise<void> {
    // A modifier une fois que la récupération des référents par structure est faite dans l'us Gestion profil utilisateur
    this.user = await this.authService.getUser();

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    if (this.beneficiary.complementId) {
      await this.getBeneficiaryComplement(this.beneficiary.complementId);
    } else {
      this.beneficiaryComplement = {
        id: undefined,
        childrenCount: undefined,
        dependentsNumber: undefined,
        qpvId: undefined,
        rsaRightBeginDate: undefined,
        currentReferent: undefined,
        reasonOrientationEnding: undefined,
        beginDateReferent: undefined,
        endDateReferent: undefined,
      } as BeneficiaryComplement;
    }
    this.initForm();
    await this.initList();
    this.orientationForm.patchValue({ structureId: this.beneficiary.orientation?.structure.id }, { emitEvent: true });

    this.structureCanUpdateSupport();
    this.isAccessModeDefault();
  }

  ngAfterViewInit(): void {
    this.beneficiariesService.accessMode.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((accessMode: string) => {
      this.accessMode = accessMode;
      this.canDisplayHistorizeButton = this.accessMode === 'default';
    });
  }

  private async openConfirmationModal(msg: string): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm(msg);
  }

  // *********************************************************************************************
  // * load data
  private async initList(): Promise<void> {
    this.supportTypes = (await this.supportTypeService.findAll()).filter((value) => value.id !== SupportTypes.HORS_ACCOMPAGNEMENT);
    if (this.user.groups.includes('STRUCTURE') && this.supportTypes.length) {
      this.supportTypes = this.supportTypes.filter((element: DisplayLabel) => element.id !== SupportTypes.PERLE);
    }

    this.structures = await this.structureService.find(true);

    if (
      this.beneficiary.orientation &&
      this.structures.filter((structure) => structure.id === this.beneficiary.orientation?.structure.id).length === 0
    ) {
      this.beneficiaryStructureWithoutBeneficiarySupport = {
        id: this.beneficiary.orientation.structure.id,
        label: this.beneficiary.orientation.structure.label,
        active: false,
      } as DisplayLabel;
      this.structures.push(this.beneficiaryStructureWithoutBeneficiarySupport);
    }

    this.healthInsurances = await this.healthInsurancesService.findAll();
    this.intensities = await this.intensityService.findAll();
  }

  private async getBeneficiaryComplement(idComplement: number): Promise<void> {
    this.beneficiaryComplement = await this.beneficiaryComplementsService.findById(idComplement);
    this.beneficiary = await this.beneficiariesService.findById(this.beneficiary.id);
    this.beneficiaryOrientations = this.beneficiary.orientations;
    this.beneficiaryOrientations.sort((a: OrientationDto, b: OrientationDto) => (a.beginDate < b.beginDate ? 1 : -1));
    this.hasHistorizedOrientations = this.beneficiaryOrientations.filter((referent) => referent.historized).length > 0;
    this.beneficiaryReferents = this.beneficiary.referents;
    this.beneficiaryReferents.sort((a: ReferentDto, b: ReferentDto) => (a.beginDate < b.beginDate ? 1 : -1));
    this.hasHistorizedReferent = this.beneficiaryReferents.filter((referent) => referent.type === 'GLOBAL' && referent.historized).length > 0;
    this.beneficiarySupports = this.beneficiary.supports;
    this.beneficiarySupports.sort((a: SupportDto, b: SupportDto) => (a.beginDate < b.beginDate ? 1 : -1));
    this.hasHistorizedSupports = this.beneficiarySupports.filter((support) => support.historized).length > 0;
    this.beneficiaryIntensities = this.beneficiary.intensities;
    this.beneficiaryIntensities.sort((a: BeneficiaryIntensityDto, b: BeneficiaryIntensityDto) => (a.beginDate < b.beginDate ? 1 : -1));
    this.hasHistorizedIntensities = this.beneficiaryIntensities.filter((intensity) => intensity.historized).length > 0;
  }

  // *********************************************************************************************
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM *
  // * FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM FORM *
  // *********************************************************************************************

  private initForm() {
    this.complementForm = this.fb.group({
      status: new FormControl<string>(this.beneficiary.status, [Validators.required]),
      maritalStatus: new FormControl<string>(this.beneficiary.maritalStatus, [Validators.required]),
      childrenCount: new FormControl<number>(this.beneficiaryComplement.childrenCount, [Validators.pattern('[0-9]*')]),
      dependentsNumber: new FormControl<number>(this.beneficiaryComplement.dependentsNumber, [Validators.pattern('[0-9]*')]),
      singleParent: new FormControl<boolean>(this.beneficiaryComplement.singleParent, [Validators.required]),
      rsaRightBeginDate: new FormControl<Date>({ value: this.beneficiaryComplement.rsaRightBeginDate, disabled: true }, [
        DateValidators.dateFormatValidator,
      ]),
      healthInsuranceId: new FormControl<number>(this.beneficiaryComplement.healthInsuranceId),

      supportForm: this.fb.group(
        {
          id: new FormControl<number | null>(this.beneficiary.support?.id),
          beginDateSupport: new FormControl<Date>(
            {
              value: this.beneficiary.support?.beginDate,
              disabled: !this.canUpdateSupOrientation() && !this.isStructureAndCanUpdateSupOrientation(),
            },
            [Validators.required, DateValidators.dateFormatValidator],
          ),
          endDateSupport: new FormControl<Date>(
            {
              value: this.beneficiary.support?.endDate,
              disabled: !this.canUpdateSupOrientation() && !this.isStructureAndCanUpdateSupOrientation(),
            },
            [DateValidators.dateFormatValidator],
          ),
          supportTypeId: new FormControl<number>(
            {
              value: this.beneficiary.support?.supportType.id,
              disabled: !this.canUpdateSupOrientation() && !this.isStructureAndCanUpdateSupOrientation(),
            },
            [Validators.required],
          ),
          typeSupportDetail: new FormControl<string>(this.beneficiary.support?.typeSupportDetail),
        },
        {
          validators: [DateValidators.datesOrderValidator('beginDateSupport', 'endDateSupport')],
        },
      ),

      intensityForm: this.fb.group(
        {
          id: new FormControl<number | null>(this.beneficiaryComplement.intensity?.id),
          beginDate: new FormControl<Date>(this.beneficiaryComplement.intensity?.beginDate, [
            DateValidators.dateFormatValidator,
            Validators.required,
          ]),
          endDate: new FormControl<Date>(this.beneficiaryComplement.intensity?.endDate, [DateValidators.dateFormatValidator]),
          intensityId: new FormControl<number>(this.beneficiaryComplement.intensity?.intensity.id, [Validators.required]),
        },
        {
          validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
        },
      ),

      orientationForm: this.fb.group(
        {
          id: new FormControl<number | null>(this.beneficiary.orientation?.id),
          orientationBeginDate: new FormControl<Date>({ value: this.beneficiary.orientation?.beginDate, disabled: !this.canUpdateSupOrientation() }, [
            Validators.required,
            DateValidators.dateFormatValidator,
          ]),
          orientationEndDate: new FormControl<Date>(
            {
              value: this.beneficiary.orientation?.endDate,
              disabled: !this.canUpdateSupOrientation() && !this.isStructureAndCanUpdateSupOrientation(),
            },
            [DateValidators.dateFormatValidator],
          ),
          structureId: new FormControl<number>({
            value: this.beneficiary.orientation?.structure.id,
            disabled: !this.canUpdateSupOrientation(),
          }),
          reasonOrientationEnding: new FormControl<string>(this.beneficiary.orientation?.reasonOrientationEnding),
        },
        {
          validators: [
            DateValidators.datesOrderValidator('orientationBeginDate', 'orientationEndDate'),
            InputValidators.endDateAndReasonValidator('orientationEndDate', 'reasonOrientationEnding'),
          ],
        },
      ),

      referentForm: this.fb.group(
        {
          id: new FormControl<number | null>(this.beneficiaryComplement.currentReferent?.id),
          beginDateReferent: new FormControl<Date>(this.beneficiaryComplement.currentReferent?.beginDate, [DateValidators.dateFormatValidator]),
          endDateReferent: new FormControl<Date>(this.beneficiaryComplement.currentReferent?.endDate, [DateValidators.dateFormatValidator]),
          referent: new FormControl<number>(this.beneficiaryComplement.currentReferent?.user.id),
        },
        {
          validators: [DateValidators.datesOrderValidator('beginDateReferent', 'endDateReferent')],
        },
      ),
    });
    this.orientationForm.controls.structureId.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(async (structureId: number) => {
      if (structureId == null) {
        return;
      }
      // so desactivate select if user isn't in same beneficary structure and he is not admin or direction+
      if (this.user.structureId !== structureId && !this.user?.groups.some((group) => ['ADMIN', 'DIRECTION+'].includes(group))) {
        this.referentForm.disable();
      }

      // Don't remove current referents on init
      this.userList = await this.userService.findAll(structureId);

      if (structureId === this.beneficiaryComplement.currentReferent?.user.structureId) {
        return;
      }

      let rsjReferent = this.beneficiaryReferents.find((referent) => referent.type === 'RSJ' && !referent.historized);
      if (rsjReferent) {
        this.beneficiaryReferents.splice(
          this.beneficiaryReferents.findIndex((item: ReferentDto) => {
            return item.id === rsjReferent.id;
          }),
          1,
        );
      }
      let currentReferent = this.beneficiaryReferents.find((referent) => referent.type === 'GLOBAL' && !referent.historized);
      if (currentReferent) {
        this.beneficiaryReferents.splice(
          this.beneficiaryReferents.findIndex((item: ReferentDto) => {
            return item.id === currentReferent.id;
          }),
          1,
        );
      }
      this.resetCurrentReferent();
    });
  }

  private isAccessModeDefault(): void {
    if (this.accessMode === 'participant' || this.accessMode === 'retroactive') {
      this.supportForm.controls.supportTypeId.disable();
      this.supportForm.controls.beginDateSupport.disable();
      this.supportForm.controls.endDateSupport.disable();
    }
  }

  protected structureCanUpdateSupport(): void {
    const isStructure = this.user.groups.includes('STRUCTURE');
    const isEditableSupport = [4, 6, 8, 23].includes(this.supportForm.controls.supportTypeId?.value);
    const isEndDateSupport = this.supportForm.controls.endDateSupport?.value;

    if (isStructure && !isEditableSupport && isEndDateSupport === undefined) {
      this.supportForm.controls.supportTypeId.disable();
      this.supportForm.controls.beginDateSupport.disable();
      this.supportForm.controls.endDateSupport.disable();
    }

    if (isStructure && isEditableSupport) {
      this.supportForm.controls.supportTypeId.enable();
      this.supportForm.controls.beginDateSupport.enable();
      this.supportForm.controls.endDateSupport.enable();
    }
  }

  public canUpdateSupOrientation(): boolean {
    return this.user?.groups?.some((group: string) => ['ADMIN', 'DIRECTION+', 'TERRITORY'].includes(group));
  }

  public isStructure(): boolean {
    return this.user.groups.includes('STRUCTURE');
  }

  public isStructureAndCanUpdateSupOrientation(): boolean {
    return (
      this.isStructure() &&
      [SupportTypes.ITE_ID_RENFORCE_HORS_RSA, SupportTypes.ITE_ID_AUTRE, SupportTypes.RSJ_ID, SupportTypes.SOCIO_PRO].includes(
        this.beneficiary.support.supportType.id,
      )
    );
  }

  // *********************************************************************************************
  // * Form onChange functions

  public beginDateReferentOnChange(): void {
    this.referentForm.controls.referent.clearValidators();
    if (this.referentForm.controls.beginDateReferent.value) {
      this.referentForm.controls.referent.setValidators([Validators.required]);
    }
    this.referentForm.controls.referent.updateValueAndValidity();
  }

  public referentOnChange(): void {
    this.referentForm.controls.beginDateReferent.clearValidators();
    if (this.referentForm.controls.referent.value) {
      this.referentForm.controls.beginDateReferent.setValidators([DateValidators.dateFormatValidator, Validators.required]);
      this.referentForm.controls.endDateReferent.setValidators([DateValidators.dateFormatValidator]);
    }
    this.referentForm.controls.beginDateReferent.updateValueAndValidity();
  }

  // *********************************************************************************************
  // * Reset form data and validator

  public async cancel(): Promise<void> {
    this.complementForm.patchValue(this.beneficiaryComplement, { emitEvent: true });
    this.complementForm.patchValue(this.beneficiary, { emitEvent: true });
    this.supportForm.patchValue(this.beneficiary, { emitEvent: true });
    this.orientationForm.patchValue(
      {
        ...this.beneficiary.orientation,
        orientationBeginDate: this.beneficiary.orientation?.beginDate,
        orientationEndDate: this.beneficiary.orientation?.endDate,
        reasonOrientationEnding: this.beneficiary.orientation?.reasonOrientationEnding,
        structureId: this.beneficiary.orientation?.structure?.id,
      },
      { emitEvent: true },
    );
    this.referentForm.patchValue(
      {
        ...this.beneficiaryComplement.currentReferent,
        beginDateReferent: this.beneficiaryComplement.currentReferent?.beginDate,
        endDateReferent: this.beneficiaryComplement.currentReferent?.endDate,
        referent: this.beneficiaryComplement.currentReferent?.user?.id,
      },
      { emitEvent: true },
    );
    this.intensityForm.patchValue(
      {
        ...this.beneficiaryComplement.intensity,
        intensityId: this.beneficiaryComplement.intensity?.intensity?.id,
      },
      { emitEvent: true },
    );
    this.supportForm.patchValue(
      {
        ...this.beneficiary.support,
        beginDateSupport: this.beneficiary.support?.beginDate,
        endDateSupport: this.beneficiary.support?.endDate,
        supportTypeId: this.beneficiary.support?.supportType?.id,
      },
      { emitEvent: true },
    );
    this.resetSupportFormValidator();
    this.resetOrientationFormValidator();
    this.resetReferentFormValidator();
    this.beneficiaryOrientations = this.beneficiary.orientations;
    this.beneficiaryReferents = this.beneficiary.referents ?? [];
    this.beneficiaryIntensities = this.beneficiary.intensities ?? [];
    this.beneficiarySupports = this.beneficiary.supports ?? [];
    this.submitted = false;
    this.isInvalidSupport = false;
    this.referentHistoryInvalid = false;
    this.intensityHistoryInvalid = false;
    this.orientationHistoryInvalid = false;
    this.supportHistoryInvalid = false;
  }

  public resetValidator(): void {
    if (!this.orientationForm.controls.orientationEndDate.value) {
      this.orientationForm.controls.reasonOrientationEnding.clearValidators();
      this.orientationForm.controls.reasonOrientationEnding.updateValueAndValidity();
    }
  }

  private resetOrientationFormValidator(): void {
    this.orientationForm.controls.orientationEndDate.clearValidators();
    this.orientationForm.controls.orientationEndDate.setValidators([DateValidators.dateFormatValidator]);
    this.orientationForm.controls.orientationEndDate.updateValueAndValidity();
    this.orientationForm.controls.structureId.clearValidators();
    this.orientationForm.controls.structureId.updateValueAndValidity({ emitEvent: false });
    this.orientationForm.controls.reasonOrientationEnding.clearValidators();
    this.orientationForm.controls.reasonOrientationEnding.updateValueAndValidity();
    if (!this.canUpdateSupOrientation()) {
      this.orientationForm.controls.orientationBeginDate.disable();
    }
  }

  public resetSupportFormValidator(): void {
    this.supportForm.get('endDateSupport').clearValidators();
    this.supportForm.get('endDateSupport').setValidators([DateValidators.dateFormatValidator]);
    this.supportForm.get('endDateSupport').updateValueAndValidity();
  }

  private resetCurrentReferent(): void {
    this.referentForm.controls.id.reset();
    this.referentForm.controls.beginDateReferent.reset();
    this.referentForm.controls.endDateReferent.reset();
    this.referentForm.controls.referent.reset();
  }

  private resetCurrentIntensity(): void {
    this.intensityForm.controls.id.reset();
    this.intensityForm.controls.beginDate.reset();
    this.intensityForm.controls.endDate.reset();
    this.intensityForm.controls.intensityId.reset();
  }

  // *********************************************************************************************
  // * Set form validator

  private setReferentValidator(fromOrientation: boolean = false, isAdmin: boolean = false): void {
    if (!isAdmin) {
      if (!fromOrientation) {
        this.referentForm.controls.endDateReferent.setValidators([Validators.required, DateValidators.dateFormatValidator]);
        this.referentForm.controls.endDateReferent.updateValueAndValidity();
      }
      this.referentForm.controls.beginDateReferent.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.referentForm.controls.referent.setValidators([Validators.required]);
      this.referentForm.controls.beginDateReferent.updateValueAndValidity();
      this.referentForm.controls.referent.updateValueAndValidity();
    }
  }

  private setEndDateStructureValidator(): void {
    this.orientationForm.controls.orientationEndDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
    this.orientationForm.controls.orientationEndDate.updateValueAndValidity();
    this.orientationForm.controls.structureId.setValidators([Validators.required]);
    this.orientationForm.controls.structureId.updateValueAndValidity();
  }

  private setEndDateSupportValidator(): void {
    this.supportForm.get('endDateSupport').setValidators([Validators.required, DateValidators.dateFormatValidator]);
    this.supportForm.get('endDateSupport').updateValueAndValidity();
  }

  public resetReferentFormValidator(full: boolean = false): void {
    if (full) {
      this.referentForm.controls.beginDateReferent.clearValidators();
      this.referentForm.controls.beginDateReferent.updateValueAndValidity();
      this.referentForm.controls.referent.clearValidators();
      this.referentForm.controls.referent.updateValueAndValidity();
    }
    this.referentForm.controls.endDateReferent.clearValidators();
    this.referentForm.controls.endDateReferent.setValidators([DateValidators.dateFormatValidator]);
    this.referentForm.controls.endDateReferent.updateValueAndValidity();
  }

  // *********************************************************************************************
  // * Form validation

  public statusValidation(): boolean {
    const today: Date = moment(this.dateService.getCurrent()).hour(0).minute(0).second(0).toDate();
    const orientationEndDate: Date = moment(this.orientationForm.controls.orientationEndDate.value).hour(0).toDate();
    const endDateSupport: Date = moment(this.supportForm.controls.endDateSupport.value).hour(0).toDate();
    if (this.complementForm.controls.status.value === 'Closed') {
      if (
        !this.supportForm.controls.endDateSupport.value ||
        endDateSupport > today ||
        !this.orientationForm.controls.orientationEndDate.value ||
        orientationEndDate > today ||
        !this.orientationForm.controls.reasonOrientationEnding.value
      ) {
        this.toastService.show("Merci de mettre des dates de fin d'accompagnement et d'orientation en cohérence avec le statut 'Clos'", 'danger');
        return false;
      }
    }
    if (
      this.supportForm.controls.endDateSupport.value &&
      endDateSupport <= today &&
      this.orientationForm.controls.orientationEndDate.value &&
      orientationEndDate <= today &&
      this.orientationForm.controls.reasonOrientationEnding.value
    ) {
      if (this.complementForm.controls.status.value !== 'Closed') {
        this.toastService.show("Merci de clore le dossier pour être en cohérence avec l'accompagnement et l'orientation.", 'danger');
        return false;
      }
    }

    // In the event of a reopenig, structures must create new support and orientation
    if (
      this.isStructure() &&
      this.beneficiary.status == 'Closed' &&
      this.complementForm.value.status !== 'Closed' &&
      (this.supportForm.value.id || this.orientationForm.value.id)
    ) {
      this.toastService.show('Un dossier ne peut être rouvert qu’avec un nouvel accompagnement et une nouvelle orientation.', 'danger');
      return false;
    }
    return true;
  }

  public intensityValidation(): boolean {
    if (
      (this.intensityForm.value.endDate && !this.supportForm.getRawValue().endDateSupport) ||
      (this.intensityForm.value.endDate != null && this.intensityForm.value.endDate < this.supportForm.getRawValue().endDateSupport)
    ) {
      return false;
    }
    return true;
  }

  private itemHistorizableHistoryValidation(itemHistorizable: ItemHistorizable[], formBeginDate: AbstractControl): boolean {
    let invalidHistory: boolean = false;
    for (let i: number = 0; i < itemHistorizable.length; i++) {
      if (
        !itemHistorizable[i].beginDate ||
        !itemHistorizable[i].endDate ||
        moment(itemHistorizable[i].beginDate).isSameOrAfter(itemHistorizable[i].endDate) ||
        (i + 1 < itemHistorizable.length && moment(itemHistorizable[i].beginDate).isSameOrBefore(itemHistorizable[i + 1].endDate))
      ) {
        invalidHistory = true;
      }
    }
    if (itemHistorizable[0] && formBeginDate.value && moment(formBeginDate.value).startOf('day').isSameOrBefore(itemHistorizable[0].endDate)) {
      invalidHistory = true;
    }
    return invalidHistory;
  }

  // *********************************************************************************************
  // * Form simple accessor

  public get orientationForm(): FormGroup {
    return this.complementForm.controls.orientationForm;
  }

  public get supportForm(): FormGroup {
    return this.complementForm.controls.supportForm;
  }

  public get referentForm(): FormGroup {
    return this.complementForm.controls.referentForm;
  }

  public get intensityForm(): FormGroup {
    return this.complementForm.controls.intensityForm;
  }

  // *********************************************************************************************
  // * UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDAT *
  // * UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDATE UPDAT *
  // *********************************************************************************************

  // *********************************************************************************************
  // * Submit form control data before update beneficiary

  public async onSubmit(): Promise<void> {
    this.supportHistoryInvalid = this.itemHistorizableHistoryValidation(
      this.beneficiarySupports.filter((support) => support.historized),
      this.supportForm.controls.beginDateSupport,
    );

    this.orientationHistoryInvalid = this.itemHistorizableHistoryValidation(
      this.beneficiaryOrientations.filter((orientation) => orientation.historized),
      this.orientationForm.controls.orientationBeginDate,
    );

    this.referentHistoryInvalid = this.itemHistorizableHistoryValidation(
      this.beneficiaryReferents.filter((referent) => referent.type === 'GLOBAL' && referent.historized),
      this.referentForm.controls.beginDateReferent,
    );

    this.intensityHistoryInvalid = this.itemHistorizableHistoryValidation(
      this.beneficiaryIntensities.filter((intensity) => intensity.historized),
      this.intensityForm.controls.beginDate,
    );

    this.submitted = true;
    if (
      !this.complementForm.valid ||
      this.orientationHistoryInvalid ||
      this.supportHistoryInvalid ||
      this.referentHistoryInvalid ||
      this.intensityHistoryInvalid ||
      this.isInvalidSupport ||
      !this.statusValidation() ||
      !this.intensityValidation()
    ) {
      this.toastService.show('La sauvegarde a échouée, veuillez vérifier les données.', 'danger');
      return;
    }

    this.resetOrientationFormValidator();
    this.resetSupportFormValidator();
    this.resetValidator();
    this.resetReferentFormValidator();
    this.beginDateReferentOnChange();
    this.referentOnChange();

    let complementBeneficiaryUpdated: BeneficiaryComplementSaveDto = {
      maritalStatus: this.complementForm.controls.maritalStatus.value,
      status: this.complementForm.controls.status.value,
      childrenCount: Number(this.complementForm.controls.childrenCount.value),
      dependentsNumber: Number(this.complementForm.controls.dependentsNumber.value),
      healthInsuranceId: this.complementForm.controls.healthInsuranceId.value,
      singleParent: this.complementForm.controls.singleParent.value,
      beginDateSupport: this.supportForm.controls.beginDateSupport.value,
      endDateSupport: this.supportForm.controls.endDateSupport.value,
      supportTypeId: this.supportForm.controls.supportTypeId.value,
      typeSupportDetail: this.supportForm.controls.typeSupportDetail.value,
    } as BeneficiaryComplementSaveDto;

    complementBeneficiaryUpdated = Utils.normalizeStrings(complementBeneficiaryUpdated);
    const complement: Complement = {
      beneficiaryComplement: complementBeneficiaryUpdated,
    } as Complement;

    await this.update(complement);
  }

  private async update(complement: Complement) {
    try {
      const beneficiaryComplementId: number = await this.beneficiaryComplementsService.updateComplementBeneficiary(this.beneficiary.id, complement);
      let beneficiaryToSave: BeneficiaryDto = JSON.parse(JSON.stringify(this.beneficiary));
      this.updateReferents(beneficiaryToSave);
      this.updateIntensities(beneficiaryToSave);
      this.updateOrientations(beneficiaryToSave);
      this.updateSupports(beneficiaryToSave);

      await this.beneficiariesService.updateBeneficiary(beneficiaryToSave);
      await this.getBeneficiaryComplement(beneficiaryComplementId);
    } catch (error) {
      this.toastService.show('La sauvegarde a échouée, veuillez vérifier les données.', 'danger');
      return;
    }
    // usefull to reset form with new data (referent, intensity...)
    this.cancel();
    this.toastService.show('Le complément du bénéficiaire a été sauvegardé avec succès.', 'success');
  }

  // *********************************************************************************************
  // * Update beneficiary sub data

  private updateReferents(beneficiaryToSave: BeneficiaryDto) {
    beneficiaryToSave.referents = this.beneficiaryReferents;
    // Delete a referent
    if (
      !this.referentForm.controls.referent.value &&
      this.beneficiaryReferents.findIndex((referent) => referent.type === 'GLOBAL' && !referent.historized) >= 0
    ) {
      this.beneficiaryReferents.splice(
        this.beneficiaryReferents.findIndex((referent) => referent.type === 'GLOBAL' && !referent.historized),
        1,
      );
      // Update intensity that already exist in DB
    } else if (this.referentForm.controls.id.value) {
      let referent: ReferentDto = this.beneficiaryReferents.find((referent) => referent.id === this.referentForm.controls.id.value);
      referent.user = { id: this.referentForm.controls.referent.value, structureId: this.orientationForm.value.structureId } as UserDto;
      referent.beginDate = this.referentForm.controls.beginDateReferent.value;
      referent.endDate = this.referentForm.controls.endDateReferent.value;
    } else if (this.referentForm.controls.referent.value) {
      // Create a new Referent
      let newReferent: ReferentDto = {
        id: this.referentForm.controls.id.value,
        user: { id: this.referentForm.controls.referent.value, structureId: this.orientationForm.value.structureId } as UserDto,
        beginDate: this.referentForm.controls.beginDateReferent.value,
        endDate: this.referentForm.controls.endDateReferent.value,
        type: 'GLOBAL',
        beneficiaryId: this.beneficiary.id,
        historized: false,
      } as ReferentDto;
      beneficiaryToSave.referents = this.beneficiaryReferents.concat(newReferent);
    }
  }

  private updateIntensities(beneficiaryToSave: BeneficiaryDto) {
    beneficiaryToSave.intensities = this.beneficiaryIntensities;
    // Delete an intensity
    if (!this.intensityForm.controls.intensityId.value && this.beneficiaryIntensities.findIndex((intensity) => !intensity.historized) >= 0) {
      this.beneficiaryIntensities.splice(
        this.beneficiaryIntensities.findIndex((intensity) => !intensity.historized),
        1,
      );
      // Update intensity that already exist in DB
    } else if (this.intensityForm.controls.id.value) {
      let intensity: BeneficiaryIntensityDto = this.beneficiaryIntensities.find((intensity) => intensity.id === this.intensityForm.controls.id.value);
      intensity.intensity = { id: this.intensityForm.controls.intensityId.value } as IntensityDto;
      intensity.beginDate = this.intensityForm.controls.beginDate.value;
      intensity.endDate = this.intensityForm.controls.endDate.value;
      // Create a new Intensity
    } else if (this.intensityForm.controls.intensityId.value) {
      let newIntensity: BeneficiaryIntensityDto = {
        id: this.intensityForm.controls.id.value,
        intensity: { id: this.intensityForm.controls.intensityId.value } as IntensityDto,
        beginDate: this.intensityForm.controls.beginDate.value,
        endDate: this.intensityForm.controls.endDate.value,
        beneficiaryId: this.beneficiary.id,
        historized: false,
      } as BeneficiaryIntensityDto;
      beneficiaryToSave.intensities = this.beneficiaryIntensities.concat(newIntensity);
    }
  }

  private updateOrientations(beneficiaryToSave: BeneficiaryDto) {
    beneficiaryToSave.orientations = this.beneficiaryOrientations;
    // Delete an orientation
    if (!this.orientationForm.controls.structureId.value && this.beneficiaryOrientations.findIndex((orientation) => !orientation.historized) >= 0) {
      this.beneficiaryOrientations.splice(
        this.beneficiaryOrientations.findIndex((orientation) => !orientation.historized),
        1,
      );
      // Update orientation that already exist in DB
    } else if (this.orientationForm.controls.id.value) {
      let orientation: OrientationDto = this.beneficiaryOrientations.find((orientation) => orientation.id === this.orientationForm.controls.id.value);
      orientation.structure = { id: this.orientationForm.controls.structureId.value } as StructureDto;
      orientation.beginDate = this.orientationForm.controls.orientationBeginDate.value;
      orientation.endDate = this.orientationForm.controls.orientationEndDate.value;
      orientation.reasonOrientationEnding = this.orientationForm.controls.reasonOrientationEnding.value;
    } else if (this.orientationForm.controls.structureId.value) {
      // Create a new Orientation
      let newOrientation: OrientationDto = {
        id: this.orientationForm.controls.id.value,
        structure: { id: this.orientationForm.controls.structureId.value } as StructureDto,
        beginDate: this.orientationForm.controls.orientationBeginDate.value,
        endDate: this.orientationForm.controls.orientationEndDate.value,
        reasonOrientationEnding: this.orientationForm.controls.reasonOrientationEnding.value,
        beneficiaryId: this.beneficiary.id,
        historized: false,
      } as OrientationDto;
      beneficiaryToSave.orientations = this.beneficiaryOrientations.concat(newOrientation);
    }
  }

  private updateSupports(beneficiaryToSave: BeneficiaryDto) {
    beneficiaryToSave.supports = this.beneficiarySupports;
    // Delete a support
    if (!this.supportForm.controls.supportTypeId.value && this.beneficiarySupports.findIndex((support) => !support.historized) >= 0) {
      this.beneficiarySupports.splice(
        this.beneficiarySupports.findIndex((support) => !support.historized),
        1,
      );
      // Update support that already exist in DB
    } else if (this.supportForm.controls.id.value) {
      let support: SupportDto = this.beneficiarySupports.find((support) => support.id === this.supportForm.controls.id.value);
      support.supportType = { id: this.supportForm.controls.supportTypeId.value } as SupportTypeDto;
      support.beginDate = this.supportForm.controls.beginDateSupport.value;
      support.endDate = this.supportForm.controls.endDateSupport.value;
      support.typeSupportDetail = this.supportForm.controls.typeSupportDetail.value;
      // Create a new Support
    } else if (this.supportForm.controls.supportTypeId.value) {
      let newSupport: SupportDto = {
        id: this.supportForm.controls.id.value,
        supportType: { id: this.supportForm.controls.supportTypeId.value } as SupportTypeDto,
        beginDate: this.supportForm.controls.beginDateSupport.value,
        endDate: this.supportForm.controls.endDateSupport.value,
        typeSupportDetail: this.supportForm.controls.typeSupportDetail.value,
        beneficiaryId: this.beneficiary.id,
        historized: false,
      } as SupportDto;
      beneficiaryToSave.supports = this.beneficiarySupports.concat(newSupport);
    }
  }

  // *********************************************************************************************
  // * HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY H *
  // * HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY HISTORY H *
  // *********************************************************************************************

  // *********************************************************************************************
  // * Historize handle functions

  public historizeReferent(historizationFromOrientation?: boolean): void {
    if (
      this.user?.groups &&
      !(this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group)) || !historizationFromOrientation)
    ) {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser le référent du bénéficiaire.", 'danger');
    }
    if (
      !this.user.groups.includes('ADMIN') ||
      (this.referentForm.controls.beginDateReferent.value && this.referentForm.controls.referent) ||
      !historizationFromOrientation
    ) {
      this.setReferentValidator(historizationFromOrientation);
      if (this.referentForm.valid) {
        this.addNewReferentHistory(historizationFromOrientation);
        this.resetCurrentReferent();
        this.resetReferentFormValidator(true);
        this.submitted = false;
        this.hasHistorizedReferent = true;
      }
    }
  }

  public historizeRsjReferent(historizationFromOrientation?: boolean): void {
    if (
      this.user?.groups &&
      !(this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group)) || !historizationFromOrientation)
    ) {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser le référent du bénéficiaire.", 'danger');
      return;
    }
    if (this.referentForm.valid) {
      let rsjReferent = this.beneficiaryReferents.find((referent) => referent.type === 'RSJ' && !referent.historized);
      if (!rsjReferent) {
        return;
      }
      rsjReferent.endDate = this.orientationForm.controls.orientationEndDate.value;
      rsjReferent.historized = true;
    }
  }

  public historizeIntensity(): void {
    if (!this.intensityForm.valid || !this.intensityForm.controls.endDate.value) {
      this.toastService.show("Le formulaire d'intensité est invalide. La date de fin de l'intensité est requise pour historiser.", 'danger');
      return;
    }
    if (!this.user?.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser l'intensité du bénéficiaire.", 'danger');
      return;
    }

    this.addNewIntensityHistory();
    this.resetCurrentIntensity();
    this.submitted = false;
    this.hasHistorizedIntensities = true;
  }

  public historizeOrientation(): void {
    //this.setEndDateStructureValidator();
    const isEditableSupport = [4, 6, 8, 23].includes(this.supportForm.controls.supportTypeId?.value);
    const isEndDateSupport = this.supportForm.controls.endDateSupport?.value;
    if (this.user.groups.includes('STRUCTURE') && !isEditableSupport && isEndDateSupport === undefined) {
      return this.toastService.show('Cette orientation peut être modifiée uniquement par Iodas', 'danger');
    }
    if (!this.orientationForm.controls.reasonOrientationEnding.value || !this.orientationForm.controls.orientationEndDate.value) {
      this.toastService.show(
        "Le formulaire d'orientation est invalide. La date de fin d'orientation et le motif de fin sont requis pour historiser.",
        'danger',
      );
      return;
    }
    if (!this.user?.groups.some((group: string) => ['ADMIN', 'TERRITORY', 'DIRECTION+', 'STRUCTURE'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser l'orientation du bénéficiaire.", 'danger');
      return;
    }

    this.historizeReferent(true);
    this.historizeRsjReferent(true);
    this.addNewOrientationHistory();
    this.orientationForm.reset();
    if (this.user.groups.includes('STRUCTURE')) {
      this.orientationForm.patchValue({ structureId: this.user.structureId }, { emitEvent: true });
      this.orientationForm.controls.orientationBeginDate.enable();
      this.orientationForm.controls.orientationEndDate.enable();
    }
    this.submitted = false;
    this.hasHistorizedOrientations = true;
  }

  public historizeSupport(): void {
    const isEditableSupport = [4, 6, 8, 23].includes(this.supportForm.controls.supportTypeId?.value);
    const isEndDateSupport = this.supportForm.controls.endDateSupport?.value;
    if (this.user.groups.includes('STRUCTURE') && !isEditableSupport && isEndDateSupport === undefined) {
      return this.toastService.show('Cet accompagnement peut être modifié uniquement par Iodas', 'danger');
    }
    if (!this.supportForm.valid || !this.supportForm.controls.endDateSupport.value) {
      this.toastService.show("Le formulaire d'accompagnement est invalide. La date de fin d'accompagnement est requise pour historiser.", 'danger');
      return;
    }
    if (!this.user?.groups.some((group: string) => ['STRUCTURE', 'ADMIN', 'TERRITORY', 'DIRECTION+'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser l'accompagnement du bénéficiaire.", 'danger');
      return;
    }

    this.addNewSupportHistory();
    this.supportForm.reset();
    if (this.user.groups.includes('STRUCTURE')) {
      this.supportForm.enable();
    }
    this.submitted = false;
    this.hasHistorizedSupports = true;
  }

  // *********************************************************************************************
  // * Delete history

  public async deleteHistorizableItemHistory(historizableItems: ItemHistorizable[], historizableItemId: number, message: string): Promise<void> {
    if (this.user?.groups?.includes('ADMIN')) {
      const confirm: boolean = await this.openConfirmationModal(message);
      if (confirm) {
        historizableItems.splice(
          historizableItems.findIndex((item: ItemHistorizable) => {
            return item.id === historizableItemId;
          }),
          1,
        );
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation de supprimer une historisation.", 'danger');
    }
  }

  // *********************************************************************************************
  // * Historize beneficiary sub data

  private addNewOrientationHistory(): void {
    const structure: DisplayLabel = this.structures.filter((item: DisplayLabel) => item.id === this.orientationForm.controls.structureId.value)[0];
    if (this.orientationForm.controls.id.value) {
      let beneficiaryOrientation: OrientationDto = this.beneficiaryOrientations.find(
        (orientation) => orientation.id === this.orientationForm.controls.id.value,
      );
      beneficiaryOrientation.structure = structure as StructureDto;
      beneficiaryOrientation.beginDate = this.orientationForm.controls.orientationBeginDate.value;
      beneficiaryOrientation.endDate = this.orientationForm.controls.orientationEndDate.value;
      beneficiaryOrientation.reasonOrientationEnding = this.orientationForm.controls.reasonOrientationEnding.value;
      beneficiaryOrientation.historized = true;
    } else {
      let newHistorizedOrientation: OrientationDto = {
        id: this.orientationForm.controls.id.value,
        structure: { id: structure.id } as StructureDto,
        beginDate: this.orientationForm.controls.orientationBeginDate.value,
        endDate: this.orientationForm.controls.orientationEndDate.value,
        beneficiaryId: this.beneficiary.id,
        reasonOrientationEnding: this.orientationForm.controls.reasonOrientationEnding.value,
        historized: true,
      } as OrientationDto;
      this.beneficiaryOrientations.push(newHistorizedOrientation);
    }
    this.beneficiaryOrientations.sort((a: OrientationDto, b: OrientationDto) => (a.beginDate < b.beginDate ? 1 : -1));
  }

  private addNewSupportHistory(): void {
    const supportType: DisplayLabel = this.supportTypes.filter((item: DisplayLabel) => item.id === this.supportForm.controls.supportTypeId.value)[0];
    if (this.supportForm.controls.id.value) {
      let beneficiarySupport: SupportDto = this.beneficiarySupports.find((support) => support.id === this.supportForm.controls.id.value);
      beneficiarySupport.supportType = supportType as SupportTypeDto;
      beneficiarySupport.beginDate = this.supportForm.controls.beginDateSupport.value;
      beneficiarySupport.endDate = this.supportForm.controls.endDateSupport.value;
      beneficiarySupport.typeSupportDetail = this.supportForm.controls.typeSupportDetail.value;
      beneficiarySupport.historized = true;
    } else {
      let newHistorizedSupport: SupportDto = {
        id: this.supportForm.controls.id.value, // tricks car vaut undefined
        supportType: supportType as SupportTypeDto,
        beginDate: this.supportForm.controls.beginDateSupport.value,
        endDate: this.supportForm.controls.endDateSupport.value,
        beneficiaryId: this.beneficiary.id,
        typeSupportDetail: this.supportForm.controls.typeSupportDetail.value,
        historized: true,
      } as SupportDto;

      this.beneficiarySupports.push(newHistorizedSupport);
    }
    this.beneficiarySupports.sort((a: SupportDto, b: SupportDto) => (a.beginDate < b.beginDate ? 1 : -1));
  }

  private addNewReferentHistory(historizedFromOrientation?: boolean): void {
    let endDateReferent: any;
    if (historizedFromOrientation) {
      endDateReferent = this.orientationForm.controls.orientationEndDate.value;
    } else {
      endDateReferent = this.referentForm.controls.endDateReferent.value;
    }

    const user: UserDto = this.userList.filter((item: UserDto) => item.id === this.referentForm.controls.referent.value)[0];
    if (this.referentForm.controls.id.value) {
      let referent = this.beneficiaryReferents.find((referent) => referent.id === this.referentForm.controls.id.value);
      referent.user = user;
      referent.beginDate = this.referentForm.controls.beginDateReferent.value;
      referent.endDate = endDateReferent;
      referent.historized = true;
    } else {
      let newHistorizedReferent: ReferentDto = {
        id: this.referentForm.controls.id.value,
        user: user,
        beginDate: this.referentForm.controls.beginDateReferent.value,
        endDate: endDateReferent,
        beneficiaryId: this.beneficiary.id,
        type: 'GLOBAL',
        historized: true,
      } as ReferentDto;
      this.beneficiaryReferents.push(newHistorizedReferent);
    }
    this.beneficiaryReferents.sort((a: ReferentDto, b: ReferentDto) => (a.beginDate < b.beginDate ? 1 : -1));
  }

  private addNewIntensityHistory(): void {
    const intensity: IntensityDto = this.intensities.filter((item: IntensityDto) => item.id === this.intensityForm.controls.intensityId.value)[0];
    if (this.intensityForm.controls.id.value) {
      let beneficiaryIntensity: BeneficiaryIntensityDto = this.beneficiaryIntensities.find(
        (intensity) => intensity.id === this.intensityForm.controls.id.value,
      );
      beneficiaryIntensity.intensity = intensity;
      beneficiaryIntensity.beginDate = this.intensityForm.controls.beginDate.value;
      beneficiaryIntensity.endDate = this.intensityForm.controls.endDate.value;
      beneficiaryIntensity.historized = true;
    } else {
      let newHistorizedIntensity: BeneficiaryIntensityDto = {
        id: this.intensityForm.controls.id.value,
        intensity: intensity,
        beginDate: this.intensityForm.controls.beginDate.value,
        endDate: this.intensityForm.controls.endDate.value,
        beneficiaryId: this.beneficiary.id,
        historized: true,
      } as BeneficiaryIntensityDto;
      this.beneficiaryIntensities.push(newHistorizedIntensity);
    }
    this.beneficiaryIntensities.sort((a: BeneficiaryIntensityDto, b: BeneficiaryIntensityDto) => (a.beginDate < b.beginDate ? 1 : -1));
  }
}
